import '@pikpak/xlco-login-ui-component/dist/style.css'
import {
  appendGlobalExtData,
  getGlobal,
  nextTick,
  removeLocaleFromPath,
  useDomain,
  useNuxtApp,
  watch,
} from '#imports'
import {
  googleClientID,
  loginInitParams,
  loginUIConfig,
  webClientId,
} from '@/config'
import type { PPLogin } from '@pikpak/xlco-login-ui-component'
import {
  ErrorType,
  type ResponseError,
} from '@xbase/common'
import type {
  LoginUI,
  LoginUIInitResult,
} from '@xbase/login-ui'
import { CustomEventType, RoutePath } from '~/types/enum'

/**
 * 登录态相关的事件处理
 *
 * @param loginUI { LoginUI } 登录组件实例
 * @param callback {VoidFunction} 回调
 */
export const loginEventHandler = (() => {
  let haveHandle = false
  return async (loginUI: LoginUI, callback?: VoidFunction) => {
    const { LoginUIEvent } = (await import('@xbase/login-ui'))
    loginUI.on(LoginUIEvent.LOGIN_SUCCESS, async () => {
      callback?.()
      if (haveHandle) { return }
      console.log('event: sign in succeed')
      haveHandle = true
      window.dispatchEvent(new CustomEvent(CustomEventType.AFTER_LOGIN))
    })
    loginUI.on(LoginUIEvent.LOGIN_FAILED, () => {
      console.log('event: sign in failed')
      window.dispatchEvent(new CustomEvent(CustomEventType.LOGIN_FAILED))
    })
    loginUI.on(LoginUIEvent.LOGOUT_SUCCESS, () => {
      console.log('event: sign out succeed')
      haveHandle = false
      window.dispatchEvent(new CustomEvent(CustomEventType.LOGOUT_SUCCESS))
    })
    loginUI.on(LoginUIEvent.LOGOUT_FAILED, () => {
      console.log('event: sign out failed')
    })
  }
})()

/**
 * login功能相关的逻辑处理以及初始化、挂载等功能入口
 * @param uiComponentId { string } login component mount element selector
 * @param callback {function}
 */
export const handleAndMountLoginPopup = async (uiComponentId?: string, callback?: VoidFunction) => {
  const loginUI = new (await import('@xbase/login-ui')).LoginUI(loginUIConfig)
  if (import.meta.client) {
    watch(() => useNuxtApp().$i18n?.locale.value, val => val && (loginUI.locale = val))
  }

  // 注入变量给sdk使用
  (loginUI.login as PPLogin).getInjectedConfig = () => ({
    clientID: loginUIConfig.loginConfig.consumerContext.client_id,
    redirectURI: `https://${useDomain().value}`,
    apiOrigins: loginInitParams.loginInitOptions.apiOrigins,
    googleClientID,
    showGoogleInMobile: false,
  })
  const loginUIInitResult: LoginUIInitResult = await loginUI?.init(loginInitParams)
  await loginEventHandler(loginUI, callback)
  if (
    loginUIInitResult.error
    && (loginUIInitResult.error as ResponseError).error === ErrorType.ABORTED
  ) {
    window.dispatchEvent(new CustomEvent(CustomEventType.AFTER_LOGIN))
    appendGlobalExtData({ userid: await loginUI.login.oAuth2Client.getSub() })
    callback?.()
  }
  // 如果已经登录
  if (await loginUI.isLoginStateValid()) {
    /* 第三方登录成功后 */
    window.dispatchEvent(new CustomEvent(CustomEventType.AFTER_LOGIN))
    appendGlobalExtData({ userid: await loginUI.login.oAuth2Client.getSub() })
    callback?.()
  } else {
    nextTick(async () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line import/extensions
      const { createLoginUIComponentFunction, startGoogleOneTapLogin } = (await import('@pikpak/xlco-login-ui-component'))
      // NOTE: 首页、URL CHECKER 页加载完成后, 允许弹出 Google One Tap
      const whiteList = [RoutePath.HOME, RoutePath.URL_CHECKER]
      if (!uiComponentId && whiteList.includes(removeLocaleFromPath(getGlobal().location?.pathname || ''))) {
        await startGoogleOneTapLogin({
          googleClientID,
          clientID: webClientId,
          apiOrigins: loginInitParams.loginInitOptions.apiOrigins,
          loginUI,
          showInMobile: false,
        })
      }
      await loginUI.showUI(createLoginUIComponentFunction, uiComponentId || '')
      // eslint-disable-next-line
      const changeLanguage = createLoginUIComponentFunction.callbackHandler()
      window.getLoginEmphasize = createLoginUIComponentFunction.getCurrentLoginPane as () => 'oauth' | 'email' | 'register' | 'phone' | 'password-login' | 'qrcode'

      watch(() => useNuxtApp().$i18n?.locale.value, val => val && changeLanguage(val), { immediate: true })
    })
  }
  return loginUI
}

/**
 * 在页面挂载后, 将login组件挂载到指定的节点上
 * @param elSelector { string } 需要挂载login组件的节点css选择器
 * @param callback
 */
export const useLogin = (elSelector: string, callback?: VoidFunction) => handleAndMountLoginPopup(elSelector, callback)
